import React, { useState, useEffect, useMemo } from 'react';
import { HealthProfile, HealthRecord, Gender } from '../healthTypes';
import { healthStorageService } from '../services/healthStorageService';
import { ProfileForm } from './ProfileForm';
import { RecordForm } from './RecordForm';
import { BMIGauge } from './BMIGauge';
import { calculateAge, getStatusColor, getStatusLabel } from '../utils/healthUtils';
import { GROWTH_STANDARDS } from '../healthConstants';
import { Plus, ChevronRight, UserCircle2, Trash2, ArrowUpRight, ArrowDownRight, Minus, Activity, TrendingUp, Baby, Home, Check } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ComposedChart } from 'recharts';
import { Member } from '../types';
import { getCheckInImageUrl } from '../services/supabaseClient';

interface HealthViewProps {
  members: Member[];
  currentMemberId: string;
  onBackToHome: () => void;
}

export const HealthView: React.FC<HealthViewProps> = ({ members, currentMemberId, onBackToHome }) => {
  const [profiles, setProfiles] = useState<HealthProfile[]>([]);
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);
  const [records, setRecords] = useState<HealthRecord[]>([]);

  const [activeTab, setActiveTab] = useState<'home' | 'analysis' | 'profiles'>('home');
  const [chartType, setChartType] = useState<'weight' | 'bmi'>('weight');
  const [analysisView, setAnalysisView] = useState<'trend' | 'growth'>('growth');
  const [growthMetric, setGrowthMetric] = useState<'height' | 'weight'>('height');

  const [showProfileForm, setShowProfileForm] = useState(false);
  const [showRecordForm, setShowRecordForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState<HealthProfile | undefined>(undefined);
  const [editingRecord, setEditingRecord] = useState<HealthRecord | undefined>(undefined);
  const [showMemberSwitcher, setShowMemberSwitcher] = useState(false);

  // Resolve avatar URLs for all members
  const [resolvedAvatarUrls, setResolvedAvatarUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let active = true;
    const resolveAll = async () => {
      const urls: Record<string, string> = {};
      for (const member of members) {
        if (member.avatarImage && !member.avatarImage.startsWith('data:') && !member.avatarImage.startsWith('http')) {
          const url = await getCheckInImageUrl(member.avatarImage);
          urls[member.id] = url;
        } else if (member.avatarImage) {
          urls[member.id] = member.avatarImage;
        }
      }
      if (active) setResolvedAvatarUrls(urls);
    };
    resolveAll();
    return () => { active = false; };
  }, [members]);

  const getAvatarSrc = (member: Member) => {
    return resolvedAvatarUrls[member.id] || member.avatarImage || '';
  };

  const currentMember = useMemo(() => members.find(m => m.id === currentMemberId) || members[0], [members, currentMemberId]);

  const renderMemberAvatar = (member: Member, size: string = 'w-10 h-10', textSize: string = 'text-sm') => (
    <div className={`${size} rounded-full ${member.avatarColor} flex items-center justify-center overflow-hidden flex-shrink-0`}>
      {member.avatarImage ? (
        <img src={getAvatarSrc(member)} alt="" className="w-full h-full object-cover" />
      ) : member.avatarEmoji ? (
        <span className={textSize}>{member.avatarEmoji}</span>
      ) : (
        <span className="text-white font-bold">{member.name.charAt(0)}</span>
      )}
    </div>
  );

  useEffect(() => {
    const loadProfiles = async () => {
      let loadedProfiles = await healthStorageService.getProfiles();

      const syncMembersToProfiles = async () => {
        const syncedProfiles = loadedProfiles.filter(p => p.id === currentMemberId);

        const currentMember = members.find(m => m.id === currentMemberId);

        if (currentMember && !syncedProfiles.find(p => p.id === currentMember.id)) {
          const newProfile: HealthProfile = {
            id: currentMember.id,
            name: currentMember.name,
            gender: currentMember.gender === 'female' ? Gender.Female : Gender.Male,
            birthDate: currentMember.birthDate || new Date().toISOString().split('T')[0],
            avatar: currentMember.avatarImage || currentMember.avatarEmoji || currentMember.avatarColor,
            createdAt: new Date().toISOString()
          };
          await healthStorageService.saveProfile(newProfile);
          loadedProfiles = await healthStorageService.getProfiles();
        }

        for (const member of members) {
          const existingProfile = loadedProfiles.find(p => p.id === member.id);
          if (!existingProfile) {
            const newProfile: HealthProfile = {
              id: member.id,
              name: member.name,
              gender: member.gender === 'female' ? Gender.Female : Gender.Male,
              birthDate: member.birthDate || new Date().toISOString().split('T')[0],
              avatar: member.avatarImage || member.avatarEmoji || member.avatarColor,
              createdAt: new Date().toISOString()
            };
            await healthStorageService.saveProfile(newProfile);
          } else {
            const needsUpdate =
              existingProfile.name !== member.name ||
              existingProfile.gender !== (member.gender === 'female' ? Gender.Female : Gender.Male) ||
              existingProfile.birthDate !== (member.birthDate || new Date().toISOString().split('T')[0]) ||
              existingProfile.avatar !== (member.avatarImage || member.avatarEmoji || member.avatarColor);

            if (needsUpdate) {
              const updatedProfile: HealthProfile = {
                ...existingProfile,
                name: member.name,
                gender: member.gender === 'female' ? Gender.Female : Gender.Male,
                birthDate: member.birthDate || new Date().toISOString().split('T')[0],
                avatar: member.avatarImage || member.avatarEmoji || member.avatarColor
              };
              await healthStorageService.saveProfile(updatedProfile);
              loadedProfiles = await healthStorageService.getProfiles();
            }
          }
        }

        loadedProfiles = await healthStorageService.getProfiles();
      };

      await syncMembersToProfiles();
      setProfiles(loadedProfiles);

      const savedActiveId = healthStorageService.getActiveProfileId();
      if (savedActiveId && loadedProfiles.find(p => p.id === savedActiveId)) {
        setActiveProfileId(savedActiveId);
      } else if (loadedProfiles.length > 0) {
        const currentMemberProfile = loadedProfiles.find(p => p.id === currentMemberId);
        setActiveProfileId(currentMemberProfile ? currentMemberProfile.id : loadedProfiles[0].id);
      }
    };

    loadProfiles();
  }, [members, currentMemberId]);

  useEffect(() => {
    const loadRecords = async () => {
      if (activeProfileId) {
        const records = await healthStorageService.getRecords(activeProfileId);
        setRecords(records);
        healthStorageService.setActiveProfileId(activeProfileId);
      } else {
        setRecords([]);
      }
    };
    loadRecords();
  }, [activeProfileId]);

  const activeProfile = profiles.find(p => p.id === activeProfileId);
  const activeMember = useMemo(() => members.find(m => m.id === activeProfileId) || members[0], [members, activeProfileId]);
  const latestRecord = records.length > 0 ? records[0] : null;
  const previousRecord = records.length > 1 ? records[1] : null;
  const isChild = activeProfile && calculateAge(activeProfile.birthDate) < 18;

  const handleSaveProfile = async (profile: HealthProfile) => {
    await healthStorageService.saveProfile(profile);
    const profiles = await healthStorageService.getProfiles();
    setProfiles(profiles);
    if (!activeProfileId) setActiveProfileId(profile.id);
    setShowProfileForm(false);
    setEditingProfile(undefined);
  };

  const handleDeleteProfile = async (id: string) => {
    if (confirm('确定要删除这个成员的健康档案吗？该成员的打卡数据将保留，但健康记录将被删除。')) {
      await healthStorageService.deleteProfile(id);
      const remaining = await healthStorageService.getProfiles();
      setProfiles(remaining);
      if (activeProfileId === id) {
        const currentMemberProfile = remaining.find(p => p.id === currentMemberId);
        setActiveProfileId(currentMemberProfile ? currentMemberProfile.id : (remaining.length > 0 ? remaining[0].id : null));
      }
    }
  };

  const handleSaveRecord = async (record: HealthRecord) => {
    await healthStorageService.saveRecord(record);
    const records = await healthStorageService.getRecords(activeProfileId!);
    setRecords(records);

    if (activeProfile && record.height > 0 && activeProfile.height !== record.height) {
      const updatedProfile = { ...activeProfile, height: record.height };
      await healthStorageService.saveProfile(updatedProfile);
      const profiles = await healthStorageService.getProfiles();
      setProfiles(profiles);
    }

    setShowRecordForm(false);
    setEditingRecord(undefined);
  };

  const handleDeleteRecord = async (id: string) => {
    if (confirm('删除这条记录?')) {
      await healthStorageService.deleteRecord(id);
      const records = await healthStorageService.getRecords(activeProfileId!);
      setRecords(records);
    }
  };

  const getTrendIcon = (current: number, prev: number) => {
    const diff = current - prev;
    if (Math.abs(diff) < 0.1) return <Minus size={16} className="text-slate-400" />;
    return diff > 0
      ? <ArrowUpRight size={16} className="text-red-500" />
      : <ArrowDownRight size={16} className="text-emerald-500" />;
  };

  if (members.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-50 dark:bg-slate-900 p-6 text-center">
        <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mb-6">
          <Activity size={40} />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">欢迎使用健康管理</h1>
        <p className="text-slate-500 dark:text-slate-400 mb-8 max-w-xs">您的私人身高体重管家。请先在打卡应用中创建成员。</p>
      </div>
    );
  }

  if (!activeProfile && !showProfileForm) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-50 dark:bg-slate-900 p-6 text-center">
        <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mb-6">
          <Activity size={40} />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">欢迎使用健康管理</h1>
        <p className="text-slate-500 dark:text-slate-400 mb-8 max-w-xs">您的私人身高体重管家。开始记录前，请先创建健康档案。</p>
        <button
          onClick={() => setShowProfileForm(true)}
          className="bg-emerald-500 text-white px-8 py-3 rounded-xl font-bold shadow-lg hover:bg-emerald-600 transition-colors"
        >
          创建档案
        </button>
        {showProfileForm && <ProfileForm onSave={handleSaveProfile} onCancel={() => setShowProfileForm(false)} />}
      </div>
    );
  }

  const renderHome = () => (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-slate-900">
      {/* Fixed top section */}
      <div className="flex-shrink-0 p-6 pb-0 space-y-6">
        <header className="flex justify-between items-center mb-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">你好, {activeProfile?.name}</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {new Date().toLocaleDateString('zh-CN', { month: 'long', day: 'numeric', weekday: 'long' })}
            </p>
          </div>
          <button onClick={() => setActiveTab('profiles')} className="w-10 h-10 bg-white dark:bg-slate-800 rounded-full shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden">
            {renderMemberAvatar(activeMember, 'w-10 h-10', 'text-sm')}
          </button>
        </header>

        <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-100 dark:border-slate-700">
          {latestRecord ? (
            <>
              <div className="flex justify-between items-end mb-4">
                <div>
                  <p className="text-sm text-slate-400 dark:text-slate-500 font-medium uppercase tracking-wide">当前体重</p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-4xl font-extrabold text-slate-900 dark:text-white">{latestRecord.weight}</span>
                    <span className="text-slate-500 dark:text-slate-400 font-medium">kg</span>
                    {previousRecord && (
                      <div className="ml-2 px-2 py-0.5 bg-slate-50 dark:bg-slate-700 rounded-md flex items-center gap-1 text-sm font-medium">
                        {getTrendIcon(latestRecord.weight, previousRecord.weight)}
                        <span className="text-slate-600 dark:text-slate-300">{Math.abs(latestRecord.weight - previousRecord.weight).toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-bold border ${getStatusColor(latestRecord.status)}`}>
                  {getStatusLabel(latestRecord.status)}
                </div>
              </div>

              <div className="mb-4">
                <div className="flex justify-between text-sm text-slate-500 dark:text-slate-400 mb-1">
                  <span>BMI</span>
                  <span className="font-bold text-slate-700 dark:text-slate-300">{latestRecord.bmi}</span>
                </div>
                <BMIGauge bmi={latestRecord.bmi} status={latestRecord.status} />
                {isChild && (
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-2 text-center bg-slate-50 dark:bg-slate-700 py-1 rounded">
                    已启用 {calculateAge(activeProfile!.birthDate)}岁 {activeProfile!.gender === 'male' ? '男' : '女'}童青少年标准
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                <div>
                  <p className="text-xs text-slate-400 dark:text-slate-500">身高</p>
                  <p className="font-bold text-slate-700 dark:text-slate-300">{latestRecord.height} cm</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 dark:text-slate-500">目标</p>
                  <p className="font-bold text-slate-700 dark:text-slate-300">{activeProfile?.targetWeight || '--'} kg</p>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-slate-50 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300 dark:text-slate-500">
                <Activity size={32} />
              </div>
              <p className="text-slate-500 dark:text-slate-400 mb-4">还没有记录</p>
              <button
                onClick={() => setShowRecordForm(true)}
                className="text-emerald-600 dark:text-emerald-400 font-bold text-sm hover:underline"
              >
                立即记录第一笔数据
              </button>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-slate-800 dark:text-slate-200">最近记录</h3>
          <button onClick={() => setActiveTab('analysis')} className="text-xs text-emerald-600 dark:text-emerald-400 font-medium flex items-center">
            查看全部 <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Scrollable record list */}
      <div className="flex-1 overflow-y-auto px-6 pb-24">
        <div className="space-y-3">
          {records.slice(0, 10).map(record => (
            <div key={record.id} onClick={() => { setEditingRecord(record); setShowRecordForm(true); }} className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm flex justify-between items-center cursor-pointer hover:border-emerald-200 dark:hover:border-emerald-700 transition-colors">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-10 rounded-full ${getStatusColor(record.status).split(' ')[1]}`}></div>
                <div>
                  <p className="font-bold text-slate-800 dark:text-slate-200">{record.weight} kg</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500">{record.date}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">BMI {record.bmi}</p>
                <p className={`text-xs ${getStatusColor(record.status).split(' ')[0]}`}>{getStatusLabel(record.status)}</p>
              </div>
            </div>
          ))}
          {records.length === 0 && (
            <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-4 bg-slate-50 dark:bg-slate-700 rounded-xl border border-dashed border-slate-200 dark:border-slate-600">无近期数据</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderGrowthAnalysis = () => {
    const genderKey = activeProfile!.gender === Gender.Male ? 'male' : 'female';
    const standards = GROWTH_STANDARDS[genderKey][growthMetric];

    const userPoints = records.map(r => {
      const recordDate = new Date(r.date);
      const birthDate = new Date(activeProfile!.birthDate);
      const age = (recordDate.getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
      return {
        age: parseFloat(age.toFixed(1)),
        userValue: growthMetric === 'height' ? r.height : r.weight,
      };
    }).filter(p => p.age >= 6.5 && p.age <= 18.5);

    const allData: any[] = standards.map(s => ({ ...s }));
    userPoints.forEach(up => {
      allData.push(up);
    });
    allData.sort((a, b) => a.age - b.age);

    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300">生长曲线 ({growthMetric === 'height' ? 'cm' : 'kg'})</h3>
            <div className="flex bg-slate-100 dark:bg-slate-700 p-1 rounded-lg">
              <button
                onClick={() => setGrowthMetric('height')}
                className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${growthMetric === 'height' ? 'bg-white dark:bg-slate-600 shadow text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`}
              >
                身高
              </button>
              <button
                onClick={() => setGrowthMetric('weight')}
                className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${growthMetric === 'weight' ? 'bg-white dark:bg-slate-600 shadow text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`}
              >
                体重
              </button>
            </div>
          </div>

          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={allData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis
                  dataKey="age"
                  type="number"
                  domain={[7, 18]}
                  tickCount={12}
                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                  tickLine={false}
                  axisLine={false}
                  unit="岁"
                />
                <YAxis
                  domain={['auto', 'auto']}
                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                  tickLine={false}
                  axisLine={false}
                  width={30}
                />
                <Tooltip
                  labelFormatter={(v) => `${v}岁`}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
                <Line type="monotone" dataKey="p97" stroke="#cbd5e1" strokeDasharray="5 5" strokeWidth={2} dot={false} connectNulls name="P97 (上限)" />
                <Line type="monotone" dataKey="p50" stroke="#94a3b8" strokeWidth={2} dot={false} connectNulls name="P50 (标准)" />
                <Line type="monotone" dataKey="p3" stroke="#cbd5e1" strokeDasharray="5 5" strokeWidth={2} dot={false} connectNulls name="P3 (下限)" />
                <Line
                  type="monotone"
                  dataKey="userValue"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={{ r: 4, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }}
                  connectNulls
                  name="我的记录"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 text-[10px] text-slate-400 dark:text-slate-500 text-center">
            参考标准：中国学龄儿童青少年生长发育标准
          </div>
        </div>
      </div>
    );
  };

  const renderTrendAnalysis = () => {
    const chartData = records.slice().reverse().map(r => ({
      date: r.date.slice(5),
      weight: r.weight,
      bmi: r.bmi,
    }));

    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300">
              {chartType === 'weight' ? '体重变化 (kg)' : 'BMI 变化趋势'}
            </h3>
            <div className="bg-slate-100 dark:bg-slate-700 p-1 rounded-lg flex text-xs font-bold">
              <button
                onClick={() => setChartType('weight')}
                className={`px-3 py-1.5 rounded-md transition-all ${chartType === 'weight' ? 'bg-white dark:bg-slate-600 text-emerald-600 dark:text-emerald-400 shadow-sm' : 'text-slate-400 dark:text-slate-500'}`}
              >
                体重
              </button>
              <button
                onClick={() => setChartType('bmi')}
                className={`px-3 py-1.5 rounded-md transition-all ${chartType === 'bmi' ? 'bg-white dark:bg-slate-600 text-emerald-600 dark:text-emerald-400 shadow-sm' : 'text-slate-400 dark:text-slate-500'}`}
              >
                BMI
              </button>
            </div>
          </div>

          {records.length < 2 ? (
            <div className="h-40 flex flex-col items-center justify-center text-slate-300 dark:text-slate-600">
              <TrendingUp size={32} className="mb-2" />
              <p className="text-xs">记录更多数据以查看趋势</p>
            </div>
          ) : (
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                  <YAxis
                    domain={['dataMin - 1', 'dataMax + 1']}
                    tick={{ fontSize: 10, fill: '#94a3b8' }}
                    tickLine={false}
                    axisLine={false}
                    width={30}
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    itemStyle={{ color: '#10b981', fontWeight: 'bold' }}
                  />
                  {chartType === 'weight' && activeProfile?.targetWeight && (
                    <ReferenceLine y={activeProfile.targetWeight} stroke="#fbbf24" strokeDasharray="3 3" />
                  )}
                  <Line
                    type="monotone"
                    dataKey={chartType}
                    stroke="#10b981"
                    strokeWidth={3}
                    dot={{ r: 4, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 6 }}
                    animationDuration={500}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAnalysis = () => {
    return (
      <div className="h-full bg-slate-50 dark:bg-slate-900 p-6 space-y-6 overflow-hidden">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">数据分析</h2>
            {activeProfile && (
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                当前成员: {activeProfile.name}
              </p>
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => setShowMemberSwitcher(!showMemberSwitcher)}
              className="w-10 h-10 bg-white dark:bg-slate-800 rounded-full shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden hover:shadow-md transition-all"
            >
          {renderMemberAvatar(activeMember, 'w-10 h-10', 'text-sm')}
            </button>
            {showMemberSwitcher && (
              <div className="absolute right-0 top-12 w-48 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 z-50 overflow-hidden">
                <div className="p-2 space-y-1 max-h-64 overflow-y-auto">
                  {members.map(member => {
                    const profile = profiles.find(p => p.id === member.id);
                    return (
                      <button
                        key={member.id}
                        onClick={() => {
                          if (!profile) {
                            const newProfile: HealthProfile = {
                              id: member.id,
                              name: member.name,
                              gender: member.gender === 'female' ? Gender.Female : Gender.Male,
                              birthDate: member.birthDate || new Date().toISOString().split('T')[0],
                              avatar: member.avatarImage || member.avatarEmoji || member.avatarColor,
                              createdAt: new Date().toISOString()
                            };
                            healthStorageService.saveProfile(newProfile);
                            const newProfiles = [...profiles, newProfile];
                            setProfiles(newProfiles);
                          }
                          setActiveProfileId(member.id);
                          healthStorageService.setActiveProfileId(member.id);
                          setShowMemberSwitcher(false);
                        }}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${activeProfileId === member.id ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-500 dark:border-emerald-600' : 'bg-slate-50 dark:bg-slate-700/50 border-slate-100 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                      >
                        {renderMemberAvatar(member, 'w-10 h-10', 'text-base')}
                        <div className="flex-1 text-left">
                          <p className="font-bold text-slate-800 dark:text-slate-200 text-sm">{member.name}</p>
                          {profile && (
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {calculateAge(profile.birthDate)}岁 · {profile.gender === 'male' ? '男' : '女'}
                            </p>
                          )}
                        </div>
                        {activeProfileId === member.id && (
                          <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                            <Check size={12} className="text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {isChild && (
          <div className="flex p-1 bg-slate-200 dark:bg-slate-700 rounded-xl">
            <button
              onClick={() => setAnalysisView('trend')}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${analysisView === 'trend' ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow' : 'text-slate-500 dark:text-slate-400'}`}
            >
              趋势分析
            </button>
            <button
              onClick={() => setAnalysisView('growth')}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${analysisView === 'growth' ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow' : 'text-slate-500 dark:text-slate-400'}`}
            >
              生长曲线
            </button>
          </div>
        )}

        {isChild && analysisView === 'growth' ? renderGrowthAnalysis() : renderTrendAnalysis()}
      </div>
    );
  };

  const renderProfiles = () => (
    <div className="h-full bg-slate-50 dark:bg-slate-900 p-6 space-y-6 overflow-hidden">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">成员健康档案</h2>
      </div>

      <div className="space-y-3">
        {members.map(member => {
          const profile = profiles.find(p => p.id === member.id);
          return (
            <div
              key={member.id}
              onClick={async () => {
                if (!profile) {
                  const newProfile: HealthProfile = {
                    id: member.id,
                    name: member.name,
                    gender: member.gender === 'female' ? Gender.Female : Gender.Male,
                    birthDate: member.birthDate || new Date().toISOString().split('T')[0],
                    avatar: member.avatarImage || member.avatarEmoji || member.avatarColor,
                    createdAt: new Date().toISOString()
                  };
                  await healthStorageService.saveProfile(newProfile);
                  const profiles = await healthStorageService.getProfiles();
                  setProfiles(profiles);
                }
                setActiveProfileId(member.id);
                setActiveTab('home');
              }}
              className={`p-4 rounded-xl border cursor-pointer transition-all ${activeProfileId === member.id ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-500 dark:border-emerald-600' : 'bg-white dark:bg-slate-800 border-slate-100 dark:border-slate-700 hover:border-emerald-200 dark:hover:border-emerald-700'}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  {renderMemberAvatar(member, 'w-12 h-12', 'text-lg')}
                  <div>
                    <h3 className="font-bold text-slate-800 dark:text-slate-200">{member.name}</h3>
                    {profile ? (
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {calculateAge(profile.birthDate)}岁 · {profile.gender === 'male' ? '男' : '女'}
                        {profile.targetWeight && ` · 目标 ${profile.targetWeight}kg`}
                      </p>
                    ) : (
                      <p className="text-sm text-emerald-600 dark:text-emerald-400">点击创建健康档案</p>
                    )}
                  </div>
                </div>
                {profile && (
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingProfile(profile); setShowProfileForm(true); }}
                      className="p-2 text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400"
                    >
                      <Activity size={16} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteProfile(profile.id); }}
                      className="p-2 text-slate-400 hover:text-red-600 dark:hover:text-red-400"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="h-full bg-slate-50 dark:bg-slate-900">
      {activeTab === 'home' && renderHome()}
      {activeTab === 'analysis' && renderAnalysis()}
      {activeTab === 'profiles' && renderProfiles()}

      {showProfileForm && (
        <ProfileForm
          initialData={editingProfile}
          onSave={handleSaveProfile}
          onCancel={() => { setShowProfileForm(false); setEditingProfile(undefined); }}
        />
      )}

      {showRecordForm && activeProfile && (
        <RecordForm
          profile={activeProfile}
          initialData={editingRecord}
          onSave={handleSaveRecord}
          onClose={() => { setShowRecordForm(false); setEditingRecord(undefined); }}
        />
      )}

      {activeTab === 'home' && (
        <button
          onClick={() => { setEditingRecord(undefined); setShowRecordForm(true); }}
          className="fixed bottom-24 right-6 w-14 h-14 bg-emerald-500 text-white rounded-full shadow-xl flex items-center justify-center hover:bg-emerald-600 transition-all active:scale-95 z-10"
        >
          <Plus size={28} strokeWidth={2.5} />
        </button>
      )}

      <nav className="fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 px-4 py-4 flex justify-between items-center z-20">
        <button
          onClick={onBackToHome}
          className={`flex flex-col items-center space-y-1 ${activeTab === 'home' ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`}
        >
          <Home size={24} />
          <span className="text-[10px] font-medium">主页</span>
        </button>

        <button
          onClick={() => setActiveTab('home')}
          className={`flex flex-col items-center space-y-1 ${activeTab === 'home' ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`}
        >
          <Activity size={24} />
          <span className="text-[10px] font-medium">首页</span>
        </button>

        <button
          onClick={() => setActiveTab('analysis')}
          className={`flex flex-col items-center space-y-1 ${activeTab === 'analysis' ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`}
        >
          <TrendingUp size={24} />
          <span className="text-[10px] font-medium">分析</span>
        </button>

        <button
          onClick={() => { setEditingRecord(undefined); setShowRecordForm(true); }}
          className={`flex flex-col items-center space-y-1 ${activeTab === 'home' ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`}
        >
          <Plus size={24} />
          <span className="text-[10px] font-medium">添加记录</span>
        </button>

        <button
          onClick={() => setActiveTab('profiles')}
          className={`flex flex-col items-center space-y-1 ${activeTab === 'profiles' ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`}
        >
          <UserCircle2 size={24} />
          <span className="text-[10px] font-medium">档案</span>
        </button>
      </nav>
    </div>
  );
};
